export interface EventMap extends HTMLElementEventMap {
    focusinvisible: Event;
    focusoutvisible: Event;
}

type Scope = HTMLElement | Document | Window;

export interface EventSlot extends DisposableObject {
    readonly types: (keyof EventMap)[];
    readonly capture: boolean;
    readonly scope: Scope | Scope[];
    readonly passive: boolean;
    readonly listener: (e: Event) => void;
}

export interface ListenerOptions<K extends keyof EventMap> {
    type: K | K[];
    scope?: Scope | Scope[];
    selector?: string | null;
    listener: (
        this: HTMLElement,
        rs: {
            event: EventMap[K];
            target: HTMLElement;
        }
    ) => any;
    passive?: boolean;
    capture?: boolean;
    thisArg?: any;
    once?: boolean;
    /**
     * An array to which the created DisposableObject will be added.
     */
    disposables?: DisposableObject[];
}

class EventUtil {
    private specials: { [eventType: string]: true } = {
        mouseenter: true,
        mouseleave: true,
        pointerenter: true,
        pointerleave: true,
    };

    public on<K extends keyof EventMap>(
        o: ListenerOptions<K>
    ): DisposableObject {
        var oSelector = o.selector;
        var thisArg = o.thisArg;
        var once = o.once === undefined ? false : o.once;
        var disposed = false;

        var slot: EventSlot = {
            capture: o.capture === undefined ? true : o.capture,
            types: Array.isArray(o.type) ? o.type.slice() : [o.type],
            scope: o.scope || (document as any),
            passive: o.passive === undefined ? false : o.passive,
            listener: (e: Event): void => {
                if (disposed) {
                    return;
                }
                var n = e.target as HTMLElement;

                var isSpecial = this.specials[e.type];

                if (!oSelector) {
                    o.listener.call(thisArg === undefined ? n : thisArg, {
                        event: e as any,
                        target: e.target as HTMLElement,
                    });
                    if (once) {
                        slot.dispose();
                    }
                    return;
                }

                while (n) {
                    if (
                        typeof n.matches === "function" &&
                        n.matches(oSelector)
                    ) {
                        o.listener.call(thisArg === undefined ? n : thisArg, {
                            event: e as any,
                            target: n,
                        });
                        if (once) {
                            slot.dispose();
                        }
                        return;
                    }
                    if (isSpecial) {
                        return;
                    }
                    n = n.parentElement;
                }
            },
            dispose: () => {
                if (disposed) {
                    return;
                }
                for (var type of slot.types) {
                    if (Array.isArray(slot.scope)) {
                        for (const scopeElement of slot.scope) {
                            scopeElement.removeEventListener(
                                type,
                                slot.listener,
                                slot.capture
                            );
                        }
                    } else {
                        slot.scope.removeEventListener(
                            type,
                            slot.listener,
                            slot.capture
                        );
                    }
                }

                o = null;
                thisArg = null;
                slot = null;

                disposed = true;
            },
        };

        for (var eventType of slot.types) {
            if (Array.isArray(slot.scope)) {
                for (const scopeElement of slot.scope) {
                    scopeElement.addEventListener(eventType, slot.listener, {
                        passive: slot.passive,
                        capture: slot.capture,
                    });
                }
            } else {
                slot.scope.addEventListener(eventType, slot.listener, {
                    passive: slot.passive,
                    capture: slot.capture,
                });
            }
        }

        if (o.disposables) {
            o.disposables.push(slot);
        }

        return slot;
    }
}

export const event = new EventUtil();

/**
 * @deprecated Use jsidea.event.on() instead.
 */
export const on = event.on.bind(event);
